// src/components/common/DatalistProvider.tsx
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from 'react';

export type DatalistContextValue = {
  value: string;
  setValue: React.Dispatch<React.SetStateAction<string>>;
  options: string[];
  setOptions: React.Dispatch<React.SetStateAction<string[]>>;
  listId: string;
  matched: string[];
  addOption: () => void;
};

export const DatalistContext = createContext<DatalistContextValue | null>(null);

export function useDatalist(): DatalistContextValue {
  const ctx = useContext(DatalistContext);
  if (!ctx) {
    throw new Error('useDatalist は DatalistProvider の内部でのみ使用できます');
  }
  return ctx;
}

export type DatalistProviderProps = {
  valueState: [string, React.Dispatch<React.SetStateAction<string>>];
  initialOptions?: string[];
  idPrefix?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label?: React.ReactNode;
  labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
  children?: React.ReactNode;
  className?: string;
  /** True の場合、入力は半角英数字のみ許可（A-Z a-z 0-9） */
  halfAlnumOnly?: boolean;
};

/**
 * public/datalist-demo.html の仕組みを React/TSX コンポーネント化。
 * - valueState: 親の useState ペア [value, setValue] を渡してください
 * - initialOptions: 初期の候補配列
 * - idPrefix: datalist の id の接頭辞（未指定時は useId() で一意化）
 * - inputProps: 内包 input にそのまま渡す props
 * - label/labelProps: input のラベル（任意）
 *
 * 子要素では useDatalist() で value/options/addOption などにアクセスできます。
 */
export const DatalistProvider: React.FC<DatalistProviderProps> = ({
  valueState,
  initialOptions = [],
  idPrefix,
  inputProps = {},
  label,
  labelProps = {},
  children,
  className,
  halfAlnumOnly = false,
}) => {
  if (!valueState || !Array.isArray(valueState) || valueState.length !== 2) {
    throw new Error('DatalistProvider の valueState には useState から得た [value, setValue] を渡してください');
  }
  const [value, setValue] = valueState;

  const [options, setOptions] = useState<string[]>(initialOptions);
  // initialOptions は非同期に更新されることがあるため、変更時に内部 options へ反映する
  useEffect(() => {
    setOptions((prev) => {
      const next = Array.from(new Set((initialOptions ?? []).map(String)));
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev;
      return next;
    });
  }, [initialOptions]);

  const rid = useId();
  const listId = useMemo(() => (idPrefix ? String(idPrefix) : `dl-${rid}`), [idPrefix, rid]);
  const inputId = inputProps.id ?? `in-${rid}`;

  const matched = useMemo(() => {
    if (!value) return [];
    const lower = String(value).toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(lower));
  }, [value, options]);

  const addOption = useCallback(() => {
    const v = String(value ?? '').trim();
    if (!v) return;
    setOptions((prev) => (prev.includes(v) ? prev : [...prev, v]));
  }, [value]);

  const ctx: DatalistContextValue = useMemo(
    () => ({ value, setValue, options, setOptions, listId, matched, addOption }),
    [value, options, listId, matched, addOption]
  );

  return (
    <DatalistContext.Provider value={ctx}>
      <div className={className}>
        {label ? (
          <label htmlFor={inputId} {...labelProps}>
            {label}
          </label>
        ) : null}

        <input
          id={inputId}
          type="text"
          list={listId}
          value={value}
          onChange={(e) =>
            setValue(
              halfAlnumOnly
                ? String(e.target.value).replace(/[^A-Za-z0-9]/g, '')
                : e.target.value
            )
          }
          autoComplete="off"
          {...inputProps}
          pattern={halfAlnumOnly ? '^[A-Za-z0-9]*$' : inputProps?.pattern}
          title={
            halfAlnumOnly
              ? (inputProps?.title ?? '半角英数字のみ入力できます')
              : inputProps?.title
          }
        />

        <datalist id={listId}>
          {options.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>

        {children}
      </div>
    </DatalistContext.Provider>
  );
};

export default DatalistProvider;